/*
 * What a parent-tree page does when the server refuses its email/text link token.
 * Signed out: the page shows the rejection with a sign-in button. Signed in: the
 * parent is moved to the same page without the token; if that player isn't theirs,
 * on to their players list. See "Rejected credentials — sign-in vs. link" in CLAUDE.md.
 */
import React, { useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { PARENT_HOME_ROUTE } from '../../../_configuration';
import { IntlMsg } from '../../components/_core/IntlMsg/intl-msg.component';
import { IIntlMessage } from '../../components/_core/IntlMsg/intl-msg.types';
import { messages } from '../../components/_core/AccessLinkRejected/access-link-rejected.messages';
import { readAccessLinkFailure } from '../helpers/graphql-error.helpers';
import { parentLoginPathWithNext } from '../helpers/parent-links.helpers';
import { parentUser, treeFromPath } from '../services/auth.service';
import { AuthFailureReason } from '../types/auth-failure.types';
import { SessionType } from '../types/session.types';
import { OUTCOME_SNACKBAR_MS } from '../snackbar.constants';
import { useSnackbarDismissAction } from './use-snackbar-dismiss-action';

interface IRejectedLinkState {
  rejectedAccessLink?: AuthFailureReason;
}

interface IUseRejectedAccessLinkArgs {
  token: string | null | undefined;
  error: unknown;
  loading: boolean;
}

const isExpired = (reason: AuthFailureReason) => reason === AuthFailureReason.ACCESS_LINK_EXPIRED;

export const useRejectedAccessLink = ({ token, error, loading }: IUseRejectedAccessLinkArgs) => {
  const history = useHistory();
  const location = useLocation<IRejectedLinkState | undefined>();
  const parent = useReactiveVar(parentUser);
  const { enqueueSnackbar } = useSnackbar();
  const dismissAction = useSnackbarDismissAction();

  const inParentTree = treeFromPath(location.pathname) === SessionType.PARENT;
  const linkFailure = inParentTree && token ? readAccessLinkFailure(error) : null;
  const carriedFailure = !token ? location.state?.rejectedAccessLink : undefined;
  const carriedFailureRefused = !!carriedFailure && !loading && !!error;

  const tokenlessParams = new URLSearchParams(location.search);
  tokenlessParams.delete('token');
  const tokenlessSearch = tokenlessParams.toString() ? `?${tokenlessParams.toString()}` : '';

  useEffect(() => {
    if (!linkFailure || !parent) return;
    history.replace({ pathname: location.pathname, search: tokenlessSearch, state: { rejectedAccessLink: linkFailure } });
  }, [linkFailure, parent, history, location.pathname, tokenlessSearch]);

  useEffect(() => {
    if (!carriedFailure || loading) return;
    const notify = (message: IIntlMessage) => enqueueSnackbar(<IntlMsg {...message} />,
      { variant: 'info', autoHideDuration: OUTCOME_SNACKBAR_MS, action: dismissAction });

    if (error) {
      notify(isExpired(carriedFailure) ? messages.expiredTitle : messages.invalidTitle);
      history.replace(PARENT_HOME_ROUTE);
    } else {
      notify(isExpired(carriedFailure) ? messages.expiredViewingSignedIn : messages.invalidViewingSignedIn);
      history.replace({ pathname: location.pathname, search: location.search, state: undefined });
    }
  }, [carriedFailure, loading, error, enqueueSnackbar, dismissAction, history, location.pathname, location.search]);

  return {
    // Only a signed-out visitor sees the rejection; a signed-in parent is being moved on.
    rejectedLink: linkFailure && !parent ? linkFailure : null,
    signInPath: parentLoginPathWithNext(location.pathname, tokenlessSearch),
    redirecting: (!!linkFailure && !!parent) || carriedFailureRefused,
  };
};
